import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, Download } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { getSessionId } from '../../services/api-client';
import { buildRegisterShopTx } from '../../services/sui-service';
import { signAndExecuteTransaction } from '../../services/zklogin-signer';
import { upsertShop } from '../../services/shop-service';
import LogoUpload from './LogoUpload';
import { ConsentCheckbox } from '../consent/ConsentCheckbox';
import { MERCHANT_TERMS_VERSION } from '../../content/legal';
import { recordConsent } from '../../services/consent-service';
import { isSessionError } from '../../services/session-errors';
import { SessionRecoveryBanner } from '../dashboard/SessionRecoveryBanner';
import { startGoogleLogin } from '../../services/zklogin-auth';

type Step = 'form' | 'confirm' | 'registering' | 'success' | 'error';

function ShopRegistration() {
    const { userAddress, logout } = useAuthStore();
    const navigate = useNavigate();
    const [step, setStep] = useState<Step>('form');
    const [error, setError] = useState<string | null>(null);
    const [sessionBroken, setSessionBroken] = useState(false);
    const [createdShopOwner, setCreatedShopOwner] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        location: '',
    });
    const [logoFile, setLogoFile] = useState<File | null>(null);
    // Optional shop coordinates so customers can find this shop via the
    // "nearby shops" discovery (Plan §2 Layer 1). Opt-in — the shop works fine
    // without them (QR + name search still function).
    const [coords, setCoords] = useState<{ lat: number; lon: number } | null>(null);
    const [geoStatus, setGeoStatus] = useState<'idle' | 'loading' | 'error'>('idle');
    // Merchant terms acceptance. Never pre-ticked, and gates CONFIRM rather
    // than the form step — the owner should read it at the point they are
    // actually creating the shop, not while still typing a name.
    const [acceptedMerchantTerms, setAcceptedMerchantTerms] = useState(false);

    const [touched, setTouched] = useState<Record<string, boolean>>({});

    function pinLocation() {
        if (!navigator.geolocation) { setGeoStatus('error'); return; }
        setGeoStatus('loading');
        navigator.geolocation.getCurrentPosition(
            (pos) => { setCoords({ lat: pos.coords.latitude, lon: pos.coords.longitude }); setGeoStatus('idle'); },
            () => setGeoStatus('error'),
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
        );
    }

    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setTouched(prev => ({ ...prev, [field]: true }));
    };

    const isFormValid = formData.name.trim().length >= 3 && formData.location.trim().length >= 2;
    const nameError = touched.name && formData.name.trim().length < 3 ? 'MIN 3 CHARACTERS' : null;
    const locationError = touched.location && formData.location.trim().length < 2 ? 'MIN 2 CHARACTERS' : null;

    async function handleRegister() {
        if (!userAddress || !isFormValid || !acceptedMerchantTerms) return;

        // Clickwrap binds to the exact version rendered, so record the constant
        // this component imported — never a server-side "latest". Non-fatal:
        // failing to log the acceptance must not block shop creation, and the
        // gate re-prompts on next load if the row is missing.
        recordConsent('merchant', MERCHANT_TERMS_VERSION).catch((e) =>
            console.warn('[merchant-terms] acceptance not recorded', e),
        );

        setStep('registering');
        setError(null);

        try {
            let imageUrl = '';
            let pixelImageUrl = '';

            // Upload logo if selected (before on-chain tx)
            if (logoFile) {
                try {
                    const formPayload = new FormData();
                    formPayload.append('logo', logoFile);
                    formPayload.append('owner', userAddress);

                    const sessionId = getSessionId();
                    const uploadRes = await fetch('/api/shops/upload-logo', {
                        method: 'POST',
                        headers: sessionId ? { Authorization: `Bearer ${sessionId}` } : undefined,
                        body: formPayload,
                    });

                    if (uploadRes.ok) {
                        const uploadData = await uploadRes.json();
                        imageUrl = uploadData.url;
                        pixelImageUrl = uploadData.pixelUrl;
                        console.log('[ShopRegistration] Logo uploaded:', imageUrl);
                    }
                } catch {
                    console.warn('[ShopRegistration] Logo upload failed, continuing without image');
                }
            }

            const tx = buildRegisterShopTx(
                formData.name.trim(),
                formData.description.trim() || formData.name.trim(),
                formData.location.trim(),
                imageUrl,
            );

            const result = await signAndExecuteTransaction(tx);

            // Extract on-chain CoffeeShop object ID from ShopRegistered event
            let onChainShopId = '';
            try {
                const events = (result as any)?.events || [];
                const shopEvent = events.find((e: any) =>
                    e.type?.includes('ShopRegistered')
                );
                onChainShopId = shopEvent?.parsedJson?.shop_id || '';
                console.log('[ShopRegistration] On-chain shop ID:', onChainShopId);
            } catch { /* ignore — fallback to no ID */ }

            // If no event found, try extracting from created objects
            if (!onChainShopId) {
                try {
                    const created = (result as any)?.effects?.created || [];
                    const shopObj = created.find((o: any) =>
                        o.owner?.Shared !== undefined
                    );
                    onChainShopId = shopObj?.reference?.objectId || '';
                } catch { /* ignore */ }
            }

            // On-chain shop owner is the registering user's address
            setCreatedShopOwner(userAddress);

            // Register with Supabase so other users can discover this shop via QR/link
            const dbResult = await upsertShop({
                onchain_id: onChainShopId,
                owner_address: userAddress,
                name: formData.name.trim(),
                description: formData.description.trim() || formData.name.trim(),
                location: formData.location.trim(),
                image_url: imageUrl,
                pixel_image_url: pixelImageUrl,
                latitude: coords?.lat ?? null,
                longitude: coords?.lon ?? null,
            });

            // upsertShop throws the server's reason now; a null here means a
            // network-level failure only. Do NOT tell them to "try again" —
            // the on-chain shop already exists, so a retry mints another one.
            if (!dbResult) {
                throw new Error('Your shop was created on-chain but we could not reach the database. Check your connection and reload — do not register again, or a second shop will be created.');
            }

            setStep('success');
        } catch (err: any) {
            console.error('Registration failed:', err);
            // A stale zkLogin proof surfaces here as a raw fullnode string
            // ("Groth16 proof verify failed"). Retrying reuses the same dead
            // proof, so "TRY AGAIN" would loop forever — send them to the same
            // reset-login recovery the dashboard offers instead.
            setSessionBroken(isSessionError(err));
            setError(err.message || 'Registration failed');
            setStep('error');
        }
    }

    const handleResetLogin = async () => {
        try {
            logout();
            await startGoogleLogin();
        } catch (e) {
            console.error('[ShopRegistration] Reset login failed:', e);
            setError('Login reset failed — reload the page and try again.');
        }
    };

    const shortAddress = userAddress
        ? `${userAddress.slice(0, 8)}...${userAddress.slice(-6)}`
        : '---';

    // ── SUCCESS ──
    if (step === 'success') {
        const appUrl = import.meta.env.VITE_APP_URL || window.location.origin;
        const shopLink = createdShopOwner ? `${appUrl}/?shop=${createdShopOwner}` : '';
        const pdfUrl = createdShopOwner
            ? `/api/shops/qr-pdf?owner=${encodeURIComponent(createdShopOwner)}&name=${encodeURIComponent(formData.name || '')}`
            : '';

        const handleCopyLink = () => {
            navigator.clipboard.writeText(shopLink);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        };

        return (
            <div className="min-h-dvh bg-background text-primary flex items-center justify-center p-4 md:p-8 safe-shell">
                <div className="max-w-lg w-full relative z-10 space-y-6">
                    {/* Step indicator */}
                    <div className="flex justify-center gap-3 mb-2">
                        <div className="w-3 h-3 bg-success" />
                        <div className="w-3 h-3 bg-success" />
                        <div className="w-3 h-3 bg-success animate-pulse" />
                    </div>
                    <p className="font-heading text-[8px] text-body-grey text-center uppercase">STEP 3 OF 3 — YOU'RE LIVE!</p>

                    <div className="w-full pixel-corners bg-primary p-[2px]">
                      <div className="bg-white pixel-corners p-[18px] text-center">
                          <div className="font-heading text-4xl mb-4 text-success">&#x2713;</div>
                          <h1 className="font-grotesk font-bold text-[22px] text-primary mb-1">
                              YOU'RE LIVE!
                          </h1>
                          <p className="font-heading text-[8px] text-coral mb-6 uppercase">
                              {formData.name.toUpperCase()}
                          </p>

                          {/* QR Code */}
                          {createdShopOwner && (
                              <div className="flex flex-col items-center mb-6">
                                  <div className="p-3 bg-white pixel-corners-sm border-2 border-primary">
                                      <QRCodeSVG
                                          value={shopLink}
                                          size={160}
                                          level="M"
                                          bgColor="#ffffff"
                                          fgColor="#09090b"
                                      />
                                  </div>
                                  <p className="font-heading text-[8px] text-body-grey mt-3 uppercase">
                                      THIS IS YOUR SHOP'S QR CODE
                                  </p>
                              </div>
                          )}

                          <p className="font-grotesk font-medium text-[13px] text-body-grey mb-6">
                              Print this QR code and put it near your register. That's it.
                          </p>

                          {/* Action buttons */}
                          <div className="flex flex-col gap-3">
                              {pdfUrl && (
                                  <a
                                      href={pdfUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="w-full flex items-center justify-center gap-2 pixel-corners bg-coral text-white font-grotesk font-bold text-sm py-3 px-6 disabled:opacity-50"
                                  >
                                      <Download size={14} /> DOWNLOAD PRINTABLE QR (PDF)
                                  </a>
                              )}
                              {shopLink && (
                                  <button
                                      onClick={handleCopyLink}
                                      className="w-full flex items-center justify-center gap-2 pixel-corners bg-tab-inactive text-body-grey font-grotesk font-bold text-sm py-3 px-6 hover:bg-hairline"
                                  >
                                      <Copy size={14} /> {copied ? 'COPIED!' : 'COPY SHOP LINK'}
                                  </button>
                              )}
                              <button
                                  onClick={() => navigate('/shops/owner/dashboard', { state: { refreshShops: true } })}
                                  className="w-full pixel-corners bg-coral text-white font-grotesk font-bold text-sm py-3 px-6 disabled:opacity-50"
                              >
                                  OPEN SHOP DASHBOARD
                              </button>
                              <button
                                  onClick={() => navigate('/', { state: { refreshShops: true } })}
                                  className="w-full pixel-corners bg-tab-inactive text-body-grey font-grotesk font-bold text-sm py-3 px-6 hover:bg-hairline"
                              >
                                  BACK TO HQ
                              </button>
                          </div>
                      </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-dvh bg-background text-primary p-4 md:p-6 safe-shell">

            <div className="max-w-2xl mx-auto relative z-10">
                {/* Back button */}
                <button
                    onClick={() => navigate('/')}
                    className="font-grotesk font-bold text-[13px] text-body-grey hover:text-coral mb-2 block"
                >
                    &lt; BACK TO HQ
                </button>

                {/* Header */}
                <div className="w-full pixel-corners bg-primary p-[2px] mb-2.5">
                  <div className="bg-white pixel-corners p-[14px]">
                      <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                              <div className="text-2xl">&#9749;</div>
                              <div>
                                  <h1 className="font-grotesk font-bold text-[19px] text-primary leading-tight">
                                      BREW GUILD
                                  </h1>
                                  <p className="font-heading text-[8px] text-coral mt-1 uppercase">
                                      REGISTER YOUR COFFEE SHOP
                                  </p>
                              </div>
                          </div>
                          <button
                              onClick={() => navigate('/for-shops')}
                              className="font-grotesk font-bold text-[13px] text-coral whitespace-nowrap"
                          >
                              LEARN MORE &gt;
                          </button>
                      </div>
                  </div>
                </div>

                {/* Registration Form */}
                <div className="w-full pixel-corners bg-primary p-[2px] mb-2.5">
                  <div className="bg-white pixel-corners p-[14px]">
                      <h2 className="font-grotesk font-bold text-[17px] text-primary mb-2.5">SHOP DETAILS</h2>

                      <div className="space-y-3">
                          {/* Shop Name */}
                          <div>
                              <label className="block font-heading text-[8px] text-body-grey mb-1 uppercase">
                                  SHOP NAME *
                              </label>
                              <input
                                  type="text"
                                  value={formData.name}
                                  onChange={(e) => handleInputChange('name', e.target.value)}
                                  placeholder="DOWNTOWN CAFE"
                                  className={`w-full px-3 py-2.5 bg-background pixel-corners-sm border-2 outline-none text-primary text-sm font-grotesk placeholder:text-body-grey ${nameError ? 'border-error' : 'border-hairline focus:border-coral'}`}
                                  maxLength={100}
                              />
                              {nameError && (
                                  <p className="font-grotesk font-medium text-[13px] text-error mt-1">{nameError}</p>
                              )}
                          </div>

                          {/* Description */}
                          <div>
                              <label className="block font-heading text-[8px] text-body-grey mb-1 uppercase">
                                  DESCRIPTION
                              </label>
                              <textarea
                                  value={formData.description}
                                  onChange={(e) => handleInputChange('description', e.target.value)}
                                  placeholder="Tell customers about your brew..."
                                  className="w-full px-3 py-2.5 bg-background pixel-corners-sm border-2 border-hairline focus:border-coral outline-none text-primary text-sm font-grotesk placeholder:text-body-grey resize-none"
                                  rows={2}
                                  maxLength={500}
                              />
                              <p className="font-grotesk font-medium text-[12px] text-body-grey mt-1 text-right">
                                  {formData.description.length}/500
                              </p>
                          </div>

                          {/* Location */}
                          <div>
                              <label className="block font-heading text-[8px] text-body-grey mb-1 uppercase">
                                  LOCATION *
                              </label>
                              <input
                                  type="text"
                                  value={formData.location}
                                  onChange={(e) => handleInputChange('location', e.target.value)}
                                  placeholder="123 COLLINS ST, MELBOURNE"
                                  className={`w-full px-3 py-2.5 bg-background pixel-corners-sm border-2 outline-none text-primary text-sm font-grotesk placeholder:text-body-grey ${locationError ? 'border-error' : 'border-hairline focus:border-coral'}`}
                                  maxLength={200}
                              />
                              {locationError && (
                                  <p className="font-grotesk font-medium text-[13px] text-error mt-1">{locationError}</p>
                              )}
                              {/* Optional map pin so customers can find this shop nearby (Plan §2) */}
                              <button
                                  type="button"
                                  onClick={pinLocation}
                                  className={`mt-2 w-full flex items-center justify-center gap-2 px-3 py-2.5 pixel-corners-sm border-2 font-grotesk font-bold text-[13px] transition-colors ${
                                      coords ? 'border-coral bg-coral/[.08] text-coral' : 'border-hairline text-body-grey hover:border-coral hover:text-coral'
                                  }`}
                              >
                                  {geoStatus === 'loading'
                                      ? 'GETTING LOCATION...'
                                      : coords
                                          ? '✓ PINNED — CUSTOMERS CAN FIND YOU NEARBY'
                                          : '📍 PIN MY SHOP LOCATION (OPTIONAL)'}
                              </button>
                              {geoStatus === 'error' && (
                                  <p className="font-grotesk font-medium text-[13px] text-error mt-1">
                                      LOCATION UNAVAILABLE — YOU CAN STILL REGISTER
                                  </p>
                              )}
                          </div>

                          {/* Logo Upload */}
                          <LogoUpload onFileSelected={setLogoFile} currentFile={logoFile} />

                          {/* Preview */}
                          {formData.name && (
                              <div className="pixel-corners-sm border-2 border-hairline bg-background p-3">
                                  <p className="font-heading text-[8px] text-body-grey mb-2 uppercase">PREVIEW</p>
                                  <div className="flex items-center gap-3">
                                      {logoFile ? (
                                          <div
                                              className="w-10 h-10 pixel-corners-sm border-2 border-primary overflow-hidden flex-shrink-0"
                                          >
                                              <img
                                                  src={URL.createObjectURL(logoFile)}
                                                  alt="Preview"
                                                  className="w-full h-full object-cover"
                                                  style={{ imageRendering: 'pixelated' }}
                                              />
                                          </div>
                                      ) : (
                                          <div className="text-xl">&#9749;</div>
                                      )}
                                      <div>
                                          <p className="font-grotesk font-bold text-[15px] text-primary">{formData.name.toUpperCase()}</p>
                                          {formData.location && (
                                              <p className="font-grotesk font-medium text-[12px] text-body-grey mt-1">
                                                  {formData.location}
                                              </p>
                                          )}
                                      </div>
                                  </div>
                              </div>
                          )}
                      </div>
                  </div>
                </div>

                {/* What You Get */}
                <div className="w-full pixel-corners bg-primary p-[2px] mb-2.5">
                  <div className="bg-white pixel-corners p-[14px]">
                      <h2 className="font-grotesk font-bold text-[17px] text-primary mb-2.5">WHAT YOU GET</h2>
                      <ul className="space-y-1.5">
                          {[
                              'REAL-TIME STAMP REQUESTS FROM CUSTOMERS',
                              'CUSTOMER ANALYTICS DASHBOARD',
                              'DIGITAL LOYALTY TRACKING',
                              'AUTO-GENERATED QR CODE FOR YOUR COUNTER',
                              'REWARD ITEM BOXES FOR LOYAL CUSTOMERS',
                          ].map((item, i) => (
                              <li key={i} className="flex items-start gap-3">
                                  <span className="text-success font-heading text-[10px] mt-0.5">&#x2713;</span>
                                  <span className="font-grotesk font-medium text-[13px] text-body-grey">{item}</span>
                              </li>
                          ))}
                      </ul>
                  </div>
                </div>

                {/* Error display. A broken session can't be retried — the proof
                    is dead, so offer the reset instead of a loop. */}
                {(step === 'error' && sessionBroken) && (
                    <SessionRecoveryBanner onReset={handleResetLogin} />
                )}
                {(step === 'error' && error && !sessionBroken) && (
                    <div className="w-full pixel-corners bg-error p-[2px] mb-2.5">
                      <div className="bg-error/[.08] pixel-corners p-[14px]">
                          <p className="font-grotesk font-bold text-[15px] text-error mb-1">REGISTRATION FAILED</p>
                          <p className="font-grotesk font-medium text-[13px] text-error">{error}</p>
                          <button
                              onClick={() => setStep('form')}
                              className="font-grotesk font-bold text-[13px] text-coral mt-3"
                          >
                              TRY AGAIN
                          </button>
                      </div>
                    </div>
                )}

                {/* Submit */}
                <div className="w-full pixel-corners bg-primary p-[2px]">
                  <div className="bg-white pixel-corners p-[14px]">
                      {step === 'form' && (
                          <>
                              <button
                                  onClick={() => setStep('confirm')}
                                  disabled={!isFormValid}
                                  className={`w-full pixel-corners bg-coral text-white font-grotesk font-bold text-sm py-3 px-6 disabled:opacity-50 ${!isFormValid ? 'opacity-30 cursor-not-allowed' : ''}`}
                              >
                                  REGISTER SHOP
                              </button>
                              {!isFormValid && (
                                  <p className="font-grotesk font-medium text-[12px] text-body-grey mt-2 text-center">
                                      FILL IN ALL REQUIRED FIELDS (*)
                                  </p>
                              )}
                          </>
                      )}

                      {step === 'confirm' && (
                          <div className="space-y-4">
                              <div className="pixel-corners-sm border-2 border-hairline bg-background p-3">
                                  <p className="font-grotesk font-bold text-[15px] text-primary mb-1">CONFIRM REGISTRATION</p>
                                  <p className="font-grotesk font-medium text-[13px] text-body-grey">
                                      This will register <span className="text-white">{formData.name.toUpperCase()}</span> in the
                                      Bit Brew network. Your account will be the shop owner.
                                  </p>
                              </div>

                              <div className="pixel-corners-sm border-2 border-hairline bg-background p-3">
                                  <ConsentCheckbox
                                      id="merchant-terms"
                                      checked={acceptedMerchantTerms}
                                      onChange={setAcceptedMerchantTerms}
                                      required
                                      label={
                                          <>
                                              I agree to the{' '}
                                              <a
                                                  href="/merchant-terms"
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="text-coral underline"
                                              >
                                                  Merchant Terms
                                              </a>
                                              . (required)
                                          </>
                                      }
                                  />
                                  <p className="font-grotesk font-medium text-[12px] text-body-grey mt-3 leading-relaxed">
                                      In short: we never hand over your customers' contact details — to you or to
                                      anyone else. You reach them through Bit Brew, and the rewards they earn at
                                      your shop are yours to honour.
                                  </p>
                              </div>

                              <div className="flex gap-3">
                                  <button
                                      onClick={() => setStep('form')}
                                      className="flex-1 pixel-corners bg-tab-inactive text-body-grey font-grotesk font-bold text-sm py-3 px-6 hover:bg-hairline"
                                  >
                                      BACK
                                  </button>
                                  <button
                                      onClick={handleRegister}
                                      disabled={!acceptedMerchantTerms}
                                      className={`flex-1 pixel-corners bg-coral text-white font-grotesk font-bold text-sm py-3 px-6 disabled:opacity-50 ${!acceptedMerchantTerms ? 'opacity-30 cursor-not-allowed' : ''}`}
                                  >
                                      CONFIRM
                                  </button>
                              </div>
                          </div>
                      )}

                      {step === 'registering' && (
                          <div className="text-center py-4">
                              <div className="w-3 h-3 bg-coral animate-pulse mx-auto mb-4" />
                              <p className="font-grotesk font-bold text-[15px] text-primary">
                                  REGISTERING YOUR SHOP...
                              </p>
                          </div>
                      )}

                      <p className="font-heading text-[8px] text-body-grey text-center mt-4 uppercase">
                          ACCOUNT: {shortAddress}
                      </p>
                  </div>
              </div>
                </div>
        </div>
    );
}

export default ShopRegistration;
